import React, { useCallback, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { formatDate } from '@/utils/dateFormat'
import { useProject } from '@/hooks/useProject'
import { deactivateProject, updateProject } from '@/api/projects'
import { Project } from '@/types/index'
import { Modal } from '@/components/ui/Modal'
import { AuditHistoryTab } from '@/components/ui/AuditHistoryTab'
import { ChevronLeft, AlertCircle } from 'lucide-react'
import { useAuth } from '@/auth/useAuth'
import { UserRole } from '@/types'

// Bug #328: field names now come from the mapped `Project` type in
// `api/projects.ts`. Status arrives lowercase from the backend, so it is
// title-cased for display rather than compared against capitalised literals.
const titleCase = (value?: string): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : '-'

const isActiveStatus = (value?: string): boolean =>
  (value || '').toLowerCase() === 'active'

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { project, loading: isLoading, error, refetch } = useProject(id || '')
  // Bug #370: projects are integration-fed and edit / deactivate are
  // platform-admin only on the backend, so only platform admins see the buttons.
  const { user } = useAuth()
  const isPlatformAdmin = !!user?.roles?.includes(UserRole.PlatformAdmin)
  const [activeTab, setActiveTab] = useState<'overview' | 'audit'>('overview')
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editData, setEditData] = useState<Partial<Project>>({})
  const [saving, setSaving] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  // Bug #334: the Save button called `setEditModalOpen(false)` and nothing
  // else, so the modal collected edits and discarded them. `updateProject`
  // has been correct since #328 (it PATCHes) — it was simply never called.
  const handleSave = useCallback(async () => {
    if (!id) return
    setSaving(true)
    setActionError(null)
    try {
      await updateProject(id, {
        name: editData.name,
        description: editData.description,
        status: editData.status,
      })
      setEditModalOpen(false)
      await refetch()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to save project')
    } finally {
      setSaving(false)
    }
  }, [editData, id, refetch])

  // Was a styled button with no onClick at all. `deactivateProject` is a
  // soft-delete on the backend and was already correct (#328).
  const handleDeactivate = useCallback(async () => {
    if (!id) return
    setActionError(null)
    try {
      await deactivateProject(id)
      // The list endpoint filters out inactive projects, so there is nothing
      // useful left on this page once it succeeds.
      navigate('/projects')
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to deactivate project')
    }
  }, [id, navigate])

  if (isLoading) {
    return (
      <div style={{ padding: '24px' }}>
        <button
          onClick={() => navigate('/projects')}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: 'var(--color-magenta)',
            fontSize: '14px',
            fontWeight: 500,
            marginBottom: '20px',
            padding: 0,
            fontFamily: 'var(--font-family)',
          }}
        >
          <ChevronLeft size={18} />
          Back to projects
        </button>
        <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-dark-grey)' }}>
          Loading project...
        </div>
      </div>
    )
  }

  if (error || !project) {
    return (
      <div style={{ padding: '24px' }}>
        <button
          onClick={() => navigate('/projects')}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: 'var(--color-magenta)',
            fontSize: '14px',
            fontWeight: 500,
            marginBottom: '20px',
            padding: 0,
            fontFamily: 'var(--font-family)',
          }}
        >
          <ChevronLeft size={18} />
          Back to projects
        </button>
        <div
          style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            padding: '16px',
            display: 'flex',
            gap: '10px',
          }}
        >
          <AlertCircle size={20} style={{ color: '#dc2626', flexShrink: 0 }} />
          <div>
            <p style={{ margin: 0, color: '#dc2626', fontWeight: 500 }}>Project not found</p>
            <p style={{ margin: '4px 0 0 0', color: '#991b1b', fontSize: '13px' }}>{error || 'Unable to load project details'}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: '0' }}>
      <div style={{ padding: '24px', paddingBottom: '16px' }}>
        <button
          onClick={() => navigate('/projects')}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: 'var(--color-magenta)',
            fontSize: '14px',
            fontWeight: 500,
            marginBottom: '20px',
            padding: 0,
            fontFamily: 'var(--font-family)',
          }}
        >
          <ChevronLeft size={18} />
          Back to projects
        </button>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: 700, margin: '0 0 8px 0', color: 'var(--color-black)' }}>
              {project.name}
            </h1>
            {project.description && (
              <p style={{ margin: 0, color: 'var(--color-dark-grey)', fontSize: '14px' }}>
                {project.description}
              </p>
            )}
          </div>
          <span
            style={{
              display: 'inline-block',
              padding: '4px 10px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 600,
              backgroundColor: isActiveStatus(project.status) ? '#dbeafe' : '#f3f4f6',
              color: isActiveStatus(project.status) ? '#0284c7' : '#6b7280',
            }}
          >
            {titleCase(project.status)}
          </span>
        </div>

        {/* Action buttons */}
        {isPlatformAdmin && (
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => {
                setActionError(null)
                // Bug #334: status arrives lowercase but the select below
                // offers capitalised options, so the raw value matched no
                // option and the field rendered blank. Title-cased on the way
                // in; `toRawProject` lowercases it again on the way out.
                setEditData({ ...project, status: titleCase(project.status) })
                setEditModalOpen(true)
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: 'var(--color-magenta)',
                color: 'var(--color-white)',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
              }}
            >
              Edit
            </button>
            <button
              onClick={handleDeactivate}
              style={{
                padding: '8px 16px',
                backgroundColor: '#f3f4f6',
                color: '#6b7280',
                border: '1px solid var(--color-light-grey)',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
              }}
            >
              Deactivate
            </button>
          </div>
        )}
      </div>

      {/* Bug #334: Save and Deactivate can both fail against the API, and
          Deactivate fails outside the modal, so the error needs a home. */}
      {actionError && (
        <div
          style={{
            margin: '16px 24px 0 24px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            padding: '12px 16px',
            color: '#dc2626',
            fontSize: '13px',
          }}
        >
          {actionError}
        </div>
      )}

      {/* Tab navigation */}
      <div style={{ borderBottom: '1px solid var(--color-light-grey)', padding: '0 24px' }}>
        <div style={{ display: 'flex', gap: '24px' }}>
          {(['overview', 'audit'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '12px 0',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                color: activeTab === tab ? 'var(--color-magenta)' : 'var(--color-dark-grey)',
                borderBottom: activeTab === tab ? '2px solid var(--color-magenta)' : 'none',
                fontFamily: 'var(--font-family)',
                textTransform: 'capitalize',
              }}
            >
              {tab === 'audit' ? 'Audit history' : 'Overview'}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div style={{ padding: '24px' }}>
        {activeTab === 'overview' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
            {[
              {
                // Bug #330: was falling back to the raw client_id, which
                // showed a GUID on screen. The API resolves the name now; if
                // it is genuinely absent, '-' is more honest than an id.
                label: 'Client company',
                value: project.clientName || '-',
                isLink: !!project.clientName && !!project.clientId,
              },
              { label: 'Status', value: titleCase(project.status) },
              { label: 'Source system', value: project.sourceSystem || '-' },
              { label: 'Created', value: formatDate(project.createdAt) },
              { label: 'Last updated', value: formatDate(project.updatedAt) },
            ].map((field) => (
              <div
                key={field.label}
                style={{
                  padding: '12px',
                  backgroundColor: 'var(--color-white)',
                  borderRadius: '6px',
                  border: '1px solid var(--color-light-grey)',
                }}
              >
                <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)', marginBottom: '4px', fontWeight: 500 }}>
                  {field.label}
                </div>
                <div style={{ fontSize: '14px', color: 'var(--color-black)', fontWeight: 500 }}>
                  {field.isLink ? (
                    <a
                      href={`/companies/${project.clientId}`}
                      style={{ color: 'var(--color-magenta)', textDecoration: 'none' }}
                    >
                      {field.value}
                    </a>
                  ) : (
                    field.value
                  )}
                </div>
              </div>
            ))}

            {project.sourceUrl && (
              <a
                href={project.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '12px',
                  backgroundColor: 'var(--color-white)',
                  borderRadius: '6px',
                  border: '1px solid var(--color-light-grey)',
                  textDecoration: 'none',
                  color: 'var(--color-magenta)',
                  fontWeight: 500,
                  fontSize: '14px',
                  gridColumn: 'span 1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                View in source system
              </a>
            )}
          </div>
        )}

        {/* Bug #331: #328 left this as a placeholder because
            GET /api/v1/audit could only filter by entity_type. That filter
            now takes entity_id, so both this tab and the company one are
            wired to the same component. */}
        {activeTab === 'audit' && (
          <AuditHistoryTab entityId={project.id} entityLabel="project" />
        )}
      </div>

      {/* Edit modal */}
      <Modal
          isOpen={editModalOpen}
          title="Edit project"
          onClose={() => setEditModalOpen(false)}
          isDirty={editData.name !== project.name || editData.description !== project.description || editData.status !== project.status}
          footer={
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setEditModalOpen(false)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--color-white)',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family)',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !editData.name}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--color-magenta)',
                  color: 'var(--color-white)',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: saving || !editData.name ? 'not-allowed' : 'pointer',
                  opacity: saving || !editData.name ? 0.6 : 1,
                  fontFamily: 'var(--font-family)',
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          }
        >
          <div style={{ display: 'grid', gap: '16px' }}>
            {[
              { label: 'Project name', key: 'name' },
              { label: 'Description', key: 'description', textarea: true },
              { label: 'Status', key: 'status', select: true, options: ['Active', 'Inactive', 'Archived'] },
            ].map((field) => (
              <div key={field.key}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                  {field.label}
                </label>
                {field.select ? (
                  <select
                    value={String(editData[field.key as keyof Project] ?? '')}
                    onChange={(e) => setEditData({ ...editData, [field.key]: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--color-light-grey)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)',
                      backgroundColor: 'var(--color-white)',
                      cursor: 'pointer',
                    }}
                  >
                    {field.options?.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                ) : field.textarea ? (
                  <textarea
                    value={String(editData[field.key as keyof Project] ?? '')}
                    onChange={(e) => setEditData({ ...editData, [field.key]: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--color-light-grey)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)',
                      minHeight: '100px',
                      resize: 'vertical',
                    }}
                  />
                ) : (
                  <input
                    type="text"
                    value={String(editData[field.key as keyof Project] ?? '')}
                    onChange={(e) => setEditData({ ...editData, [field.key]: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--color-light-grey)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)',
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        </Modal>
    </div>
  )
}

export default ProjectDetail
